import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import styled from "styled-components";
import { setColor } from "../../../../../assets/styles/styles";
import { getRooms } from "../../rooms/api/roomController";
import { CACHE_QUERY_OPTIONS } from "../../../../../constants";
import Button from "../../../../../components/common/button/Button";

const RoomSearchDropdown = ({ label, updateTagState, selectedRoom }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchRoom, setSearchRoom] = useState("");
    const [filteredRoom, setFilteredRoom] = useState([]);

    const { data: roomOptions = [] } = useQuery(["rooms"], getRooms, {
        ...CACHE_QUERY_OPTIONS(),
    });

    // Ensure selectedRoom is an array
    const safeSelectedRoom = Array.isArray(selectedRoom) ? selectedRoom : [];

    const dropdownRef = useRef(null);

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setIsDropdownOpen(false);
        }
    };

    useEffect(() => {
        // console.log("selectedRoom:", selectedRoom, "safeSelectedRoom:", safeSelectedRoom);
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [selectedRoom]);

    const handleSearchRoom = (e) => {
        const searchValue = e.target.value;
        setSearchRoom(searchValue);
        if (roomOptions && searchValue) {
            const filtered = roomOptions.filter((room) => room.name?.toLowerCase().includes(searchValue.toLowerCase()));
            setFilteredRoom(filtered);
            setIsDropdownOpen(true);
        } else {
            setFilteredRoom([]);
            setIsDropdownOpen(false);
        }
    };

    const handleRoomSelect = (roomId) => {
        if (!safeSelectedRoom.includes(roomId)) {
            const newRoomIds = [...safeSelectedRoom, roomId];

            // For CreateTag, update roomIds directly as a string array
            updateTagState("roomIds", newRoomIds);

            setSearchRoom("");
            setIsDropdownOpen(false);
        }
    };

    const handleRoomRemove = (roomId) => {
        const filteredRoomIds = safeSelectedRoom.filter((item) => item !== roomId);

        updateTagState("roomIds", filteredRoomIds);
    };

    return (
        <DropdownContainer ref={dropdownRef}>
            <Label>{label}</Label>
            <InputBox type="text" placeholder="Type to search rooms" value={searchRoom} onChange={handleSearchRoom} />
            {isDropdownOpen && (
                <DropdownContentRooms>
                    {filteredRoom.length > 0 ? (
                        filteredRoom.map((option) => (
                            <DropdownItem
                                key={option.id}
                                onClick={() => handleRoomSelect(option.id)}
                                isSelected={safeSelectedRoom.includes(option.id)}
                            >
                                {option.name}
                            </DropdownItem>
                        ))
                    ) : (
                        <MessageNote>No rooms match your search</MessageNote>
                    )}
                </DropdownContentRooms>
            )}
        </DropdownContainer>
    );
};

RoomSearchDropdown.propTypes = {
    label: PropTypes.string.isRequired,
    updateTagPopupState: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
    selectedRoom: PropTypes.arrayOf(PropTypes.string),
};

export default RoomSearchDropdown;

const DropdownContainer = styled.div`
    position: relative;
`;

const Label = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
`;

const InputBox = styled.input`
    padding: 10px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    margin: 0rem 0rem 0rem 0rem;
    width: ${(props) => (props.width ? props.width : "100%")};
    background-color: transparent;
    margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
    &:disabled {
        background-color: ${setColor.grey1};
    }
`;

const DropdownItem = styled.a`
    display: block;
    padding: 10px;
    font-weight: 700;
    width: 100%;
    font-family: "Barlow-Regular", Helvetica;
    cursor: pointer;
    color: ${(props) => (props.isSelected ? `${setColor.hightlight}` : `${setColor.inputText}`)};
    background: ${(props) => (props.isSelected ? `${setColor.pinkButtonBgGradient}` : "transparent")};
    letter-spacing: 0;
    line-height: normal;
    white-space: nowrap;
    margin: 0px;
    border-radius: 5px;
    &:hover {
        color: ${(props) => (props.isSelected ? `${setColor.hightlight}` : `${setColor.accent4}`)};
    }
`;

const MessageNote = styled.div`
    font-size: 14px;
    color: ${setColor.inputText};
`;

const DropdownContentRooms = styled.div`
    position: absolute;
    padding: 5px;
    font-family: "Barlow-Regular", Helvetica;
    left: 0;
    width: 100%;
    border: 1px solid ${setColor.modalOutline};
    border-radius: 10px;
    background-color: ${setColor.buttonTextBlack};
    z-index: 1;
    max-height: 200px;
    overflow-y: auto;
`;
